import type { AssistantMessage, ToolCall } from '../../../../types'
import type { MessageGroup } from '../../../../utils/messageGrouping'

export const getChildToolCalls = (toolCalls: Array<ToolCall>, parentToolId: string): Array<ToolCall> => {
  return toolCalls.filter((tc) => tc.metadata?.parentToolId === parentToolId)
}

/**
 * All assistant tool calls in a group, in message order. The ONE walk for
 * "this group's tool calls" — `includePrimary: false` is for surfaces that
 * render the primary message's own calls separately (the message row),
 * `true` for whole-group scans (the staged-action index).
 */
export const collectGroupToolCalls = (group: MessageGroup, opts: { includePrimary: boolean }): Array<ToolCall> => {
  const toolCalls: Array<ToolCall> = []
  if (opts.includePrimary && group.primaryMessage.role === 'assistant') {
    toolCalls.push(...((group.primaryMessage as AssistantMessage).toolCalls ?? []))
  }
  for (const msg of group.submessages ?? []) {
    if (msg.role === 'assistant' && msg.toolCalls) {
      toolCalls.push(...msg.toolCalls)
    }
  }
  return toolCalls
}

/** A tool call with no parent — the ONE definition of "not a subagent child". */
export const isTopLevelToolCall = (toolCall: ToolCall): boolean => !toolCall.metadata?.parentToolId

/**
 * Set-aware variant: a call whose parent isn't in `toolCalls` is treated as
 * top-level, so an orphaned child still renders instead of vanishing.
 */
export const getTopLevelToolCalls = (toolCalls: Array<ToolCall>): Array<ToolCall> => {
  const toolCallIds = new Set(toolCalls.map((tc) => tc.id))
  return toolCalls.filter((tc) => {
    const parentToolId = tc.metadata?.parentToolId
    return !parentToolId || !toolCallIds.has(parentToolId)
  })
}

// `any`: tool results are freeform per-tool JSON; every caller narrows ad hoc.
export const parseToolCallResult = (result: string | null): any => {
  if (!result) return null
  try {
    return JSON.parse(result)
  } catch {
    return null
  }
}

// Every SSE delta rebuilds the message list, so scans that look for staged
// actions or favicons re-visit every completed tool call in the chat. Parsing
// each of their results again is O(chat) JSON.parse per token. Cache the parsed
// value on the owning object and invalidate on the source string's reference so
// a growing result still re-parses. Callers treat the value as read-only —
// mutating it would leak into every other reader of the same tool call.
const parsedJsonCache = new WeakMap<object, { source: string | null; parsed: any }>()

// `any` for the same reason as parseToolCallResult, whose value this caches.
export const parseJsonCached = (owner: object, source: string | null): any => {
  const cached = parsedJsonCache.get(owner)
  if (cached && cached.source === source) return cached.parsed
  const parsed = parseToolCallResult(source)
  parsedJsonCache.set(owner, { source, parsed })
  return parsed
}

export const parseToolCallResultCached = (toolCall: ToolCall): any => parseJsonCached(toolCall, toolCall.result ?? null)
